import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1100,
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(3),
  },
  cards: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    flexWrap: "wrap",
  },
  card: {
    padding: theme.spacing(2.5),
    minWidth: 200,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  cardLabel: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  cardValue: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  tableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
  },
  tableHead: {
    backgroundColor: theme.palette.action.hover,
  },
  tableHeadCell: {
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.palette.text.secondary,
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  tableCell: {
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
  },
  rank: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
  },
  signupCount: {
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  empty: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  emptyCode: {
    display: "inline-block",
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(0.5, 1.5),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    fontFamily: "monospace",
    fontSize: "0.85rem",
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
  error: {
    padding: theme.spacing(3),
    color: theme.palette.error.main,
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "1rem",
    marginBottom: theme.spacing(1.5),
  },
}));

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (_) {
    return "—";
  }
}

export default function CampaignAttributionPage({ campaignAttribution }) {
  const classes = useStyles();
  const { loading, error, headlines, rows } = campaignAttribution;

  if (loading) {
    return (
      <div className={classes.root}>
        <Typography variant="h5" className={classes.title}>
          Ad Attribution
        </Typography>
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.root}>
        <Typography variant="h5" className={classes.title}>
          Ad Attribution
        </Typography>
        <Typography className={classes.error}>
          Failed to load attribution data: {error}
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography variant="h5" className={classes.title}>
        Ad Attribution
      </Typography>

      <div className={classes.cards}>
        <Paper className={classes.card} elevation={0}>
          <Typography className={classes.cardLabel}>Attributed signups</Typography>
          <Typography className={classes.cardValue}>
            {headlines.totalAttributedSignups.toLocaleString()}
          </Typography>
        </Paper>
        <Paper className={classes.card} elevation={0}>
          <Typography className={classes.cardLabel}>Unique campaign keys</Typography>
          <Typography className={classes.cardValue}>
            {headlines.uniqueCampaignCount.toLocaleString()}
          </Typography>
        </Paper>
      </div>

      <Typography className={classes.sectionTitle}>
        Campaigns — sorted by signups
      </Typography>

      {rows.length === 0 ? (
        <div className={classes.empty}>
          <Typography variant="body1">No attributed signups yet.</Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Add <code>utm_source</code> and <code>utm_id</code> to your ad links to start
            tracking. Example:
          </Typography>
          <span className={classes.emptyCode}>
            https://www.ouro.is/?utm_source=reddit&amp;utm_id=ad1
          </span>
        </div>
      ) : (
        <div className={classes.tableContainer}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>#</TableCell>
                <TableCell className={classes.tableHeadCell}>Source</TableCell>
                <TableCell className={classes.tableHeadCell}>Identifier</TableCell>
                <TableCell className={classes.tableHeadCell} align="right">
                  Signups
                </TableCell>
                <TableCell className={classes.tableHeadCell}>First signup</TableCell>
                <TableCell className={classes.tableHeadCell}>Latest signup</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={`${row.source}:${row.id}`}
                  className={classes.tableRow}
                >
                  <TableCell className={`${classes.tableCell} ${classes.rank}`}>
                    {index + 1}
                  </TableCell>
                  <TableCell className={classes.tableCell}>{row.source}</TableCell>
                  <TableCell className={classes.tableCell}>{row.id}</TableCell>
                  <TableCell
                    className={`${classes.tableCell} ${classes.signupCount}`}
                    align="right"
                  >
                    {row.signupCount.toLocaleString()}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatDate(row.firstSignupAt)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatDate(row.latestSignupAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

CampaignAttributionPage.propTypes = {
  campaignAttribution: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    headlines: PropTypes.shape({
      totalAttributedSignups: PropTypes.number.isRequired,
      uniqueCampaignCount: PropTypes.number.isRequired,
    }).isRequired,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        source: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        signupCount: PropTypes.number.isRequired,
        firstSignupAt: PropTypes.string,
        latestSignupAt: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
};
